import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Progress,
  Row,
  Select,
  Space,
  Switch,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import AdaptiveTable from '../components/AdaptiveTable';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { DownOutlined, UpOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  agentPriceApi,
  agentPriceLive,
  agentServiceApi,
  billingWeightRuleApi,
  carrierApi,
  channelApi,
  AgentPriceAgentOption,
  AgentPriceAgentStatusDTO,
  AgentPricePiece,
  AgentPriceQueryRequest,
  AgentPriceRowDTO,
  BillingWeightRuleDTO,
  CarrierDTO,
} from '../api/kfc';
import { COUNTRY_LIST, countryFilterSort, formatCountryLabel, getCountryByCode } from '../constants/countries';

const COUNTRY_OPTIONS = COUNTRY_LIST.map((c) => ({ label: formatCountryLabel(c), value: c.code }));
const PACKING_OPTIONS = [
  { label: 'WPX 包裹', value: 'WPX' },
  { label: 'DOC 文件', value: 'DOC' },
  { label: 'PAK 防水袋', value: 'PAK' },
];
const GOODS_OPTIONS = [
  { label: 'PH 普货', value: 'PH' },
  { label: 'DC 带电', value: 'DC' },
  { label: 'MC 带磁', value: 'MC' },
  { label: 'LQ 液体', value: 'LQ' },
  { label: 'FM 粉末', value: 'FM' },
];
const SOURCE_LABEL: Record<string, string> = {
  EXCEL: '价目表(Excel)',
  API_SAMPLING: '价目表(API估算)',
  LIVE: '实时',
  LIVE_CACHE: '实时报价(缓存)',
};

const SOURCE_COLOR: Record<string, string> = {
  EXCEL: 'blue',
  LIVE: 'green',
  LIVE_CACHE: 'cyan',
};
const UNCLASSIFIED = '__UNCLASSIFIED__';

interface QueryFormValues {
  countryCode: string;
  pcs: number;
  weight: number;
  volume?: number;
  packing: string;
  agentIds?: string[];
  queryDate?: Dayjs;
  goodsTypes?: string[];
  postcode?: string;
  city?: string;
  state?: string;
  markupFixed?: number;
  markupPercent?: number;
}

interface QueryTab {
  key: string;
  title: string;
  mode: 'db' | 'live';
  req: AgentPriceQueryRequest;
  rows: PriceRow[];
  agents: AgentPriceAgentStatusDTO[];
  loading: boolean;
  total: number;
  done: number;
  error?: string;
}

interface TabFilter {
  carrier?: string;
  agent?: string;
  notCreatedOnly: boolean;
}

const money = (v?: number) => (v == null ? '-' : v.toFixed(2));

/** 新标签页打开渠道列表并定位该渠道，避免丢失当前查询结果 */
const channelHref = (id: string) => `/routes/channels?channelId=${encodeURIComponent(id)}`;
/** 同一代理会对同一渠道按分区返回多条且 zoneCode 为空（如百顺达美国东/西岸），业务字段拼不出唯一 key，收到数据时按序编号 */
type PriceRow = AgentPriceRowDTO & { rowKey: string };
const withRowKeys = (rows: AgentPriceRowDTO[], prefix: string): PriceRow[] =>
  rows.map((r, i) => ({ ...r, rowKey: `${prefix}#${i}` }));
const rowKey = (r: PriceRow) => r.rowKey;

const AgentPriceQuery: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm<QueryFormValues>();
  const [collapsed, setCollapsed] = useState(false);
  const [agentOptions, setAgentOptions] = useState<AgentPriceAgentOption[]>([]);
  const [carriers, setCarriers] = useState<CarrierDTO[]>([]);
  const [billingRules, setBillingRules] = useState<BillingWeightRuleDTO[]>([]);
  const [tabs, setTabs] = useState<QueryTab[]>([]);
  const [activeKey, setActiveKey] = useState<string>();
  const [filters, setFilters] = useState<Record<string, TabFilter>>({});
  const [selected, setSelected] = useState<Record<string, React.Key[]>>({});
  const [pieces, setPieces] = useState<AgentPricePiece[]>([]);
  const [piecesOpen, setPiecesOpen] = useState(false);
  const [piecesDraft, setPiecesDraft] = useState<AgentPricePiece[]>([]);
  const [remarkRow, setRemarkRow] = useState<AgentPriceRowDTO>();
  const [feeRow, setFeeRow] = useState<AgentPriceRowDTO>();
  const [createRows, setCreateRows] = useState<AgentPriceRowDTO[]>([]);
  const [createInitial, setCreateInitial] = useState<Record<string, unknown>>({});
  const [creating, setCreating] = useState(false);
  const [syncingKeys, setSyncingKeys] = useState<string[]>([]);
  const [createForm] = Form.useForm();
  const seq = useRef(0);
  const aborts = useRef<Record<string, AbortController>>({});

  useEffect(() => {
    agentPriceApi.agents().then((r) => setAgentOptions(r.data)).catch(() => message.error('加载代理列表失败'));
    carrierApi.list().then((r) => setCarriers(r.data)).catch(() => undefined);
    billingWeightRuleApi.list().then((r) => setBillingRules(r.data)).catch(() => undefined);
    const current = aborts.current;
    return () => Object.values(current).forEach((c) => c.abort());
  }, []);

  const updateTab = (key: string, fn: (t: QueryTab) => QueryTab) =>
    setTabs((prev) => prev.map((t) => (t.key === key ? fn(t) : t)));

  const buildRequest = (v: QueryFormValues): AgentPriceQueryRequest => ({
    countryCode: v.countryCode,
    pcs: v.pcs,
    weight: v.weight,
    volume: v.volume || undefined,
    packing: v.packing,
    agentIds: v.agentIds?.length ? v.agentIds : undefined,
    queryDate: v.queryDate ? v.queryDate.format('YYYY-MM-DD') : undefined,
    goodsTypes: v.goodsTypes?.length ? v.goodsTypes : undefined,
    postcode: v.postcode || undefined,
    city: v.city || undefined,
    state: v.state || undefined,
    markupFixed: v.markupFixed ?? undefined,
    markupPercent: v.markupPercent ?? undefined,
    pieces: pieces.length ? pieces : undefined,
  });

  const tabTitle = (req: AgentPriceQueryRequest, mode: 'db' | 'live') => {
    const c = getCountryByCode(req.countryCode);
    return `${mode === 'live' ? '实时' : '历史'} ${c ? c.nameZh : req.countryCode} ${req.weight}KG×${req.pcs} ${req.packing}`;
  };

  const runQuery = async (mode: 'db' | 'live') => {
    let values: QueryFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const req = buildRequest(values);
    seq.current += 1;
    const key = `q${seq.current}`;
    const tab: QueryTab = { key, title: tabTitle(req, mode), mode, req, rows: [], agents: [], loading: true, total: 0, done: 0 };
    setTabs((prev) => [...prev, tab]);
    setActiveKey(key);
    setFilters((prev) => ({ ...prev, [key]: { notCreatedOnly: false } }));
    if (mode === 'db') {
      try {
        const res = await agentPriceApi.query(req);
        updateTab(key, (t) => ({ ...t, rows: withRowKeys(res.data.rows, 'db'), agents: res.data.agents, loading: false }));
      } catch (e: any) {
        updateTab(key, (t) => ({ ...t, loading: false, error: e.response?.data?.message || '查询失败' }));
      }
      return;
    }
    const controller = new AbortController();
    aborts.current[key] = controller;
    try {
      await agentPriceLive(
        req,
        {
          onTotal: (total) => updateTab(key, (t) => ({ ...t, total })),
          onAgentResult: (agent, rows) =>
            updateTab(key, (t) => ({
              ...t,
              done: t.done + 1,
              agents: [...t.agents, agent],
              rows: [...t.rows, ...withRowKeys(rows, agent.agentId)].sort(
                (a, b) => (a.totalChargeCny ?? Number.MAX_VALUE) - (b.totalChargeCny ?? Number.MAX_VALUE)
              ),
            })),
          onDone: () => updateTab(key, (t) => ({ ...t, loading: false })),
        },
        controller.signal
      );
      updateTab(key, (t) => ({ ...t, loading: false }));
    } catch (e: any) {
      if (e?.name !== 'AbortError') {
        updateTab(key, (t) => ({ ...t, loading: false, error: e?.message || '实时查询失败' }));
      }
    } finally {
      delete aborts.current[key];
    }
  };

  const closeTab = (key: string) => {
    aborts.current[key]?.abort();
    setTabs((prev) => {
      const next = prev.filter((t) => t.key !== key);
      if (activeKey === key) setActiveKey(next.length ? next[next.length - 1].key : undefined);
      return next;
    });
  };

  const resetForm = () => {
    form.resetFields();
    setPieces([]);
  };

  // ---------------------------------------------------------------- 单件明细

  const openPieces = () => {
    setPiecesDraft(pieces.length ? pieces.map((p) => ({ ...p })) : [{ count: 1 }]);
    setPiecesOpen(true);
  };

  const applyPieces = () => {
    const valid = piecesDraft.filter((p) => p.weight && p.weight > 0);
    if (valid.length === 0) {
      setPieces([]);
      setPiecesOpen(false);
      return;
    }
    let pcs = 0;
    let weight = 0;
    let volume = 0;
    for (const p of valid) {
      const n = p.count && p.count > 0 ? p.count : 1;
      pcs += n;
      weight += (p.weight ?? 0) * n;
      if (p.length && p.width && p.height) volume += (p.length * p.width * p.height * n) / 1_000_000;
    }
    setPieces(valid);
    form.setFieldsValue({
      pcs,
      weight: Number(weight.toFixed(3)),
      volume: volume > 0 ? Number(volume.toFixed(6)) : undefined,
    });
    setPiecesOpen(false);
  };

  // ---------------------------------------------------------------- 创建渠道

  const openCreate = (rows: AgentPriceRowDTO[]) => {
    const targets = rows.filter((r) => r.kfcChannelStatus === 'NOT_CREATED');
    if (targets.length === 0) {
      message.info('所选行中没有“未建渠道”的记录');
      return;
    }
    const unique = new Map<string, AgentPriceRowDTO>();
    targets.forEach((r) => unique.set(`${r.agentId}::${r.suggestedChannelCode}`, r));
    const list = Array.from(unique.values());
    if (list.length === 1) {
      const r = list[0];
      setCreateInitial({
        agentId: r.agentId,
        channelCode: r.suggestedChannelCode,
        channelName: r.channelName,
        carrierId: r.carrierId,
        volumeCoefficient: r.volumeDivisor,
      });
    } else {
      setCreateInitial({});
    }
    setCreateRows(list);
  };

  const markCreated = (agentId: string, channelCode: string, id: string, name: string) => {
    setTabs((prev) =>
      prev.map((t) => ({
        ...t,
        rows: t.rows.map((r) =>
          r.agentId === agentId && r.suggestedChannelCode === channelCode
            ? { ...r, kfcChannelStatus: 'CREATED', kfcChannelId: id, kfcChannelName: name }
            : r
        ),
      }))
    );
  };

  /** 同步代理渠道成功后，本地把同代理同渠道的行翻转为「未建渠道」，无需重新查价 */
  const markSynced = (agentId: string, row: AgentPriceRowDTO, suggestedCode: string) => {
    const sameChannel = (r: AgentPriceRowDTO) =>
      r.agentId === agentId &&
      ((row.agentChannelCode && r.agentChannelCode === row.agentChannelCode) ||
        (!row.agentChannelCode && row.channelName && r.channelName === row.channelName));
    setTabs((prev) =>
      prev.map((t) => ({
        ...t,
        rows: t.rows.map((r) =>
          r.kfcChannelStatus === 'SERVICE_NOT_SYNCED' && sameChannel(r)
            ? { ...r, kfcChannelStatus: 'NOT_CREATED', suggestedChannelCode: suggestedCode }
            : r
        ),
      }))
    );
  };

  const syncOneChannel = async (r: PriceRow) => {
    const key = rowKey(r);
    if (syncingKeys.includes(key)) return;
    setSyncingKeys((prev) => [...prev, key]);
    try {
      const res = await agentServiceApi.syncOne({
        agentId: r.agentId,
        serviceCode: r.agentChannelCode || undefined,
        serviceName: r.channelName || undefined,
      });
      const data = res.data;
      if (!data.success) {
        message.error(data.message || '同步代理渠道失败');
        return;
      }
      message.success(data.created ? `已同步代理渠道 ${data.service.serviceCode || data.service.serviceName}` : '该渠道已同步');
      const suggested = data.service.serviceCode || data.service.serviceName || r.agentChannelCode || '';
      markSynced(r.agentId, r, suggested);
    } catch (e: any) {
      message.error(e.response?.data?.message || '同步代理渠道失败');
    } finally {
      setSyncingKeys((prev) => prev.filter((k) => k !== key));
    }
  };

  const submitCreate = async () => {
    const values = await createForm.validateFields();
    setCreating(true);
    try {
      if (createRows.length === 1) {
        const res = await channelApi.save({
          agentId: values.agentId,
          channelCode: values.channelCode,
          channelName: values.channelName,
          carrierId: values.carrierId,
          volumeCoefficient: values.volumeCoefficient,
          billingRuleId: values.billingRuleId,
        });
        markCreated(createRows[0].agentId, createRows[0].suggestedChannelCode ?? values.channelCode, res.data.id,
          res.data.channelName || res.data.channelCode);
        message.success('渠道已创建');
      } else {
        let ok = 0;
        const failed: string[] = [];
        for (const r of createRows) {
          try {
            const res = await channelApi.save({
              agentId: r.agentId,
              channelCode: r.suggestedChannelCode,
              channelName: r.channelName,
              carrierId: r.carrierId,
              volumeCoefficient: r.volumeDivisor,
              billingRuleId: values.billingRuleId,
            });
            markCreated(r.agentId, r.suggestedChannelCode ?? '', res.data.id, res.data.channelName || res.data.channelCode);
            ok += 1;
          } catch (e: any) {
            failed.push(`${r.agentId}/${r.channelName}: ${e.response?.data?.message || '失败'}`);
          }
        }
        if (failed.length) {
          Modal.warning({ title: `已创建 ${ok} 个，失败 ${failed.length} 个`, content: failed.join('\n') });
        } else {
          message.success(`已创建 ${ok} 个渠道`);
        }
      }
      setCreateRows([]);
      if (activeKey) setSelected((prev) => ({ ...prev, [activeKey]: [] }));
    } catch (e: any) {
      message.error(e.response?.data?.message || '创建失败');
    } finally {
      setCreating(false);
    }
  };

  // ---------------------------------------------------------------- 结果

  const activeTab = tabs.find((t) => t.key === activeKey);
  const activeFilter: TabFilter = (activeKey && filters[activeKey]) || { notCreatedOnly: false };
  const setActiveFilter = (patch: Partial<TabFilter>) => {
    if (!activeKey) return;
    setFilters((prev) => ({ ...prev, [activeKey]: { ...activeFilter, ...patch } }));
  };

  const carrierOrder = useMemo(() => {
    const m = new Map<string, number>();
    carriers.forEach((c) => m.set(c.id, c.sortOrder ?? 9999));
    return m;
  }, [carriers]);

  const facet = useMemo(() => {
    const rows = activeTab?.rows ?? [];
    const base = activeFilter.notCreatedOnly ? rows.filter((r) => r.kfcChannelStatus !== 'CREATED') : rows;
    const carrierCount = new Map<string, { name: string; count: number }>();
    const agentCount = new Map<string, number>();
    for (const r of base) {
      const ck = r.carrierId || UNCLASSIFIED;
      const entry = carrierCount.get(ck) ?? { name: r.carrierName || '未分类', count: 0 };
      entry.count += 1;
      carrierCount.set(ck, entry);
      if (!activeFilter.carrier || activeFilter.carrier === ck) {
        agentCount.set(r.agentId, (agentCount.get(r.agentId) ?? 0) + 1);
      }
    }
    const carriersSorted = Array.from(carrierCount.entries()).sort((a, b) => {
      if (a[0] === UNCLASSIFIED) return 1;
      if (b[0] === UNCLASSIFIED) return -1;
      return (carrierOrder.get(a[0]) ?? 9999) - (carrierOrder.get(b[0]) ?? 9999);
    });
    const visible = base.filter(
      (r) =>
        (!activeFilter.carrier || (r.carrierId || UNCLASSIFIED) === activeFilter.carrier) &&
        (!activeFilter.agent || r.agentId === activeFilter.agent)
    );
    return { carriersSorted, agentCount, visible, baseCount: base.length };
  }, [activeTab, activeFilter, carrierOrder]);

  const agentFilterOptions = useMemo(() => {
    const m = new Map<string, string>();
    (activeTab?.rows ?? []).forEach((r) => m.set(r.agentId, r.agentName || r.agentId));
    return Array.from(m.entries()).map(([value, text]) => ({ text, value }));
  }, [activeTab]);

  const columns: ColumnsType<PriceRow> = [
    {
      title: '运费API',
      dataIndex: 'agentName',
      width: 120,
      fixed: 'left',
      filters: agentFilterOptions,
      onFilter: (v, r) => r.agentId === v,
      render: (_, r) => r.agentName || r.agentId,
    },
    {
      title: 'API渠道',
      dataIndex: 'agentChannelCode',
      width: 130,
      fixed: 'left',
      sorter: (a, b) => (a.agentChannelCode ?? '').localeCompare(b.agentChannelCode ?? ''),
    },
    { title: 'API渠道描述', dataIndex: 'channelName', width: 180, ellipsis: true },
    {
      title: '数据来源',
      dataIndex: 'source',
      width: 120,
      filters: Object.entries(SOURCE_LABEL).map(([value, text]) => ({ text, value })),
      onFilter: (v, r) => r.source === v,
      render: (v: string) => <Tag color={SOURCE_COLOR[v] ?? 'gold'}>{SOURCE_LABEL[v] ?? v}</Tag>,
    },
    {
      title: '价目表日期 / 报价时间',
      dataIndex: 'effectiveDate',
      width: 150,
      render: (v: string | undefined, r) => (r.quoteTime ? dayjs(r.quoteTime).format('YYYY-MM-DD HH:mm') : v ?? '-'),
    },
    {
      title: 'KFC渠道',
      dataIndex: 'kfcChannelStatus',
      width: 160,
      filters: [
        { text: '已建渠道', value: 'CREATED' },
        { text: '未建渠道', value: 'NOT_CREATED' },
        { text: '代理渠道未同步', value: 'SERVICE_NOT_SYNCED' },
      ],
      onFilter: (v, r) => r.kfcChannelStatus === v,
      render: (_, r) => {
        if (r.kfcChannelStatus === 'CREATED') {
          return r.kfcChannelId ? (
            <Typography.Link href={channelHref(r.kfcChannelId)} target="_blank" rel="noopener noreferrer">
              {r.kfcChannelName || r.kfcChannelId}
            </Typography.Link>
          ) : r.kfcChannelName;
        }
        if (r.kfcChannelStatus === 'NOT_CREATED') return <Tag color="orange">未建渠道</Tag>;
        return (
          <Tooltip title="请先在代理列表中同步该代理的渠道">
            <Tag>代理渠道未同步</Tag>
          </Tooltip>
        );
      },
    },
    { title: '目的地', dataIndex: 'countryCode', width: 90, render: (v, r) => r.destName || v },
    {
      title: '计费重',
      dataIndex: 'chargeWeight',
      width: 90,
      align: 'right',
      sorter: (a, b) => (a.chargeWeight ?? 0) - (b.chargeWeight ?? 0),
    },
    {
      title: '单价/KG(CNY)',
      dataIndex: 'unitPriceCny',
      width: 110,
      align: 'right',
      sorter: (a, b) => (a.unitPriceCny ?? Number.MAX_VALUE) - (b.unitPriceCny ?? Number.MAX_VALUE),
      render: money,
    },
    {
      title: '总费用(CNY)',
      dataIndex: 'totalChargeCny',
      width: 120,
      align: 'right',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => (a.totalChargeCny ?? Number.MAX_VALUE) - (b.totalChargeCny ?? Number.MAX_VALUE),
      render: (v: number | undefined, r) =>
        r.missingRate ? <Tag color="red">缺汇率</Tag> : <Typography.Text strong>{money(v)}</Typography.Text>,
    },
    { title: '加价', dataIndex: 'markupCny', width: 80, align: 'right', render: money },
    {
      title: '运费(CNY)',
      dataIndex: 'freightCny',
      width: 100,
      align: 'right',
      sorter: (a, b) => (a.freightCny ?? 0) - (b.freightCny ?? 0),
      render: money,
    },
    {
      title: '附加费(CNY)',
      dataIndex: 'otherFeeCny',
      width: 110,
      align: 'right',
      render: (v: number | undefined, r) =>
        r.otherFees?.length ? <Typography.Link onClick={() => setFeeRow(r)}>{money(v)}</Typography.Link> : money(v),
    },
    { title: '运费(原币)', dataIndex: 'freightOrig', width: 100, align: 'right', render: money },
    { title: '原币', dataIndex: 'currency', width: 70 },
    { title: '运费计算公式', dataIndex: 'formula', width: 180, ellipsis: true },
    { title: '材积除以数', dataIndex: 'volumeDivisor', width: 100, align: 'right' },
    { title: '时效', dataIndex: 'transitTime', width: 110, ellipsis: true },
    { title: '查询时间(ms)', dataIndex: 'runTimeMs', width: 110, align: 'right', render: (v?: number) => v ?? '-' },
    {
      title: '备注',
      dataIndex: 'remark',
      width: 160,
      ellipsis: true,
      render: (v?: string, r?: AgentPriceRowDTO) =>
        v ? <Typography.Link onClick={() => setRemarkRow(r)}>{v}</Typography.Link> : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, r) => {
        if (r.kfcChannelStatus === 'NOT_CREATED') {
          return <Typography.Link onClick={() => openCreate([r])}>创建渠道</Typography.Link>;
        }
        if (r.kfcChannelStatus === 'CREATED' && r.kfcChannelId) {
          return (
            <Tooltip title={r.kfcChannelName}>
              <Typography.Link href={channelHref(r.kfcChannelId)} target="_blank" rel="noopener noreferrer">
                查看渠道
              </Typography.Link>
            </Tooltip>
          );
        }
        if (r.kfcChannelStatus === 'SERVICE_NOT_SYNCED') {
          return (
            <Typography.Link disabled={syncingKeys.includes(rowKey(r))} onClick={() => syncOneChannel(r)}>
              {syncingKeys.includes(rowKey(r)) ? '同步中…' : '同步代理渠道'}
            </Typography.Link>
          );
        }
        return null;
      },
    },
  ];

  const selectedKeys = (activeKey && selected[activeKey]) || [];
  const selectedRows = facet.visible.filter((r) => selectedKeys.includes(rowKey(r)));

  const renderResult = (tab: QueryTab) => {
    const failedAgents = tab.agents.filter((a) => !a.success);
    return (
      <div>
        {tab.mode === 'live' && tab.total > 0 && (
          <Progress
            percent={Math.round((tab.done / tab.total) * 100)}
            status={tab.loading ? 'active' : failedAgents.length ? 'exception' : 'success'}
            format={() => `${tab.done}/${tab.total} 个代理`}
            style={{ marginBottom: 8 }}
          />
        )}
        {tab.mode === 'live' && !tab.loading && tab.total === 0 && !tab.error && (
          <Alert type="info" showIcon message="没有可实时查价的代理（需支持查价接口并已配置 API）" style={{ marginBottom: 8 }} />
        )}
        {tab.error && <Alert type="error" showIcon message={tab.error} style={{ marginBottom: 8 }} />}
        <Space wrap style={{ marginBottom: 8 }}>
          <span>承运商：</span>
          <Button size="small" type={!activeFilter.carrier ? 'primary' : 'default'} onClick={() => setActiveFilter({ carrier: undefined })}>
            全部 ({facet.baseCount})
          </Button>
          {facet.carriersSorted.map(([key, v]) => (
            <Button
              key={key}
              size="small"
              type={activeFilter.carrier === key ? 'primary' : 'default'}
              onClick={() => setActiveFilter({ carrier: activeFilter.carrier === key ? undefined : key, agent: undefined })}
            >
              {v.name} ({v.count})
            </Button>
          ))}
        </Space>
        <br />
        <Space wrap style={{ marginBottom: 8 }}>
          <span>代理：</span>
          <Button size="small" type={!activeFilter.agent ? 'primary' : 'default'} onClick={() => setActiveFilter({ agent: undefined })}>
            全部
          </Button>
          {Array.from(facet.agentCount.entries()).map(([agentId, count]) => {
            const name = tab.rows.find((r) => r.agentId === agentId)?.agentName || agentId;
            return (
              <Button
                key={agentId}
                size="small"
                type={activeFilter.agent === agentId ? 'primary' : 'default'}
                onClick={() => setActiveFilter({ agent: activeFilter.agent === agentId ? undefined : agentId })}
              >
                {name} ({count})
              </Button>
            );
          })}
          {failedAgents.map((a) => (
            <Tooltip key={`fail-${a.agentId}`} title={a.message || '查询失败'}>
              <Button size="small" danger>
                {a.agentName || a.agentId} 失败
              </Button>
            </Tooltip>
          ))}
          <span style={{ marginLeft: 16 }}>仅看未建渠道</span>
          <Switch size="small" checked={activeFilter.notCreatedOnly} onChange={(v) => setActiveFilter({ notCreatedOnly: v })} />
          <Button size="small" disabled={selectedRows.length === 0} onClick={() => openCreate(selectedRows)}>
            批量创建渠道{selectedRows.length ? ` (${selectedRows.length})` : ''}
          </Button>
        </Space>
        <AdaptiveTable<PriceRow>
          size="small"
          rowKey={rowKey}
          virtual
          loading={tab.loading && tab.rows.length === 0}
          columns={columns}
          dataSource={facet.visible}
          pagination={false}
          scroll={{ x: 2600, y: 560 }}
          rowSelection={{
            selectedRowKeys: selectedKeys,
            onChange: (keys) => setSelected((prev) => ({ ...prev, [tab.key]: keys })),
            getCheckboxProps: (r) => ({ disabled: r.kfcChannelStatus !== 'NOT_CREATED' }),
          }}
          locale={{ emptyText: tab.loading ? '查询中…' : <Empty description="没有匹配的价格" /> }}
        />
      </div>
    );
  };

  return (
    <div>
      <Card
        size="small"
        title="查询价格"
        extra={
          <Button type="link" size="small" onClick={() => setCollapsed((v) => !v)} icon={collapsed ? <DownOutlined /> : <UpOutlined />}>
            {collapsed ? '展开' : '收起'}
          </Button>
        }
        style={{ marginBottom: 12 }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ pcs: 1, packing: 'WPX', queryDate: dayjs() }}
          style={{ display: collapsed ? 'none' : undefined }}
        >
          <Row gutter={12}>
            <Col span={4}>
              <Form.Item name="countryCode" label="目的地" rules={[{ required: true, message: '请选择目的地' }]}>
                <Select showSearch optionFilterProp="label" filterSort={countryFilterSort} options={COUNTRY_OPTIONS} placeholder="国家" />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item label="件数" required>
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item name="pcs" noStyle rules={[{ required: true, message: '请填写件数' }]}>
                    <InputNumber min={1} precision={0} style={{ width: '100%' }} disabled={pieces.length > 0} />
                  </Form.Item>
                  <Button onClick={openPieces}>{pieces.length ? `明细(${pieces.length})` : '明细'}</Button>
                </Space.Compact>
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item name="weight" label="重量(KG)" rules={[{ required: true, message: '请填写重量' }]}>
                <InputNumber min={0.001} step={0.5} style={{ width: '100%' }} disabled={pieces.length > 0} />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item name="volume" label="体积(CBM)">
                <InputNumber min={0} step={0.001} style={{ width: '100%' }} disabled={pieces.length > 0} />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item name="packing" label="包装类型" rules={[{ required: true }]}>
                <Select options={PACKING_OPTIONS} />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="agentIds" label="代理">
                <Select
                  mode="multiple"
                  allowClear
                  maxTagCount="responsive"
                  placeholder="默认全部"
                  optionFilterProp="label"
                  options={agentOptions.map((a) => ({
                    value: a.agentId,
                    label: `${a.agentName} (${a.agentId})${a.supportsQuote ? '' : ' · 仅历史数据'}`,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item name="queryDate" label="查询日期">
                <DatePicker style={{ width: '100%' }} allowClear={false} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={4}>
              <Form.Item name="goodsTypes" label="货物类型">
                <Select mode="tags" options={GOODS_OPTIONS} placeholder="默认普货" />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item name="postcode" label="邮编">
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item name="city" label="城市">
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item name="state" label="州/省">
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item name="markupFixed" label="加价+(CNY)">
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item name="markupPercent" label="加价%">
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <Space>
          <Tooltip title="并行调用各代理查价接口，结果按到达顺序显示并保存快照">
            <Button onClick={() => runQuery('live')}>实时查询</Button>
          </Tooltip>
          <Tooltip title="按价目表计算；没有价目表的渠道显示 7 天内条件完全一致的实时报价">
            <Button onClick={() => runQuery('db')}>历史数据查询</Button>
          </Tooltip>
          <Button onClick={resetForm}>重置</Button>
          <Button onClick={() => navigate('/agents/price-data?tab=classify')}>分类设置</Button>
        </Space>
      </Card>

      <Card size="small" bodyStyle={{ paddingTop: 4 }}>
        {tabs.length === 0 ? (
          <Empty description="填写条件后点击“实时查询”或“历史数据查询”" />
        ) : (
          <Tabs
            type="editable-card"
            hideAdd
            activeKey={activeKey}
            onChange={setActiveKey}
            onEdit={(key, action) => {
              if (action === 'remove' && typeof key === 'string') closeTab(key);
            }}
            items={tabs.map((t) => ({
              key: t.key,
              label: `${t.title}${t.loading ? ' …' : ` (${t.rows.length})`}`,
              children: t.key === activeKey ? renderResult(t) : null,
            }))}
          />
        )}
      </Card>

      <Modal
        title="单件明细"
        open={piecesOpen}
        width={720}
        onOk={applyPieces}
        onCancel={() => setPiecesOpen(false)}
        okText="确定"
      >
        <AdaptiveTable<AgentPricePiece & { idx: number }>
          size="small"
          rowKey="idx"
          pagination={false}
          dataSource={piecesDraft.map((p, idx) => ({ ...p, idx }))}
          columns={(
            [
              ['weight', '单件重量(KG)'],
              ['length', '长(cm)'],
              ['width', '宽(cm)'],
              ['height', '高(cm)'],
              ['count', '件数'],
            ] as [keyof AgentPricePiece, string][]
          )
            .map(([field, title]) => ({
              title,
              dataIndex: field,
              render: (_: unknown, r: AgentPricePiece & { idx: number }) => (
                <InputNumber
                  min={field === 'count' ? 1 : 0}
                  precision={field === 'count' ? 0 : undefined}
                  value={r[field]}
                  onChange={(v) =>
                    setPiecesDraft((prev) => prev.map((p, i) => (i === r.idx ? { ...p, [field]: v ?? undefined } : p)))
                  }
                  style={{ width: '100%' }}
                />
              ),
            }))
            .concat([
              {
                title: '',
                dataIndex: 'weight',
                render: (_: unknown, r: AgentPricePiece & { idx: number }) => (
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => setPiecesDraft((prev) => prev.filter((_p, i) => i !== r.idx))}
                  />
                ),
              },
            ])}
        />
        <Space style={{ marginTop: 8 }}>
          <Button icon={<PlusOutlined />} onClick={() => setPiecesDraft((prev) => [...prev, { count: 1 }])}>
            添加一行
          </Button>
          <Button onClick={() => setPiecesDraft([])}>清空明细</Button>
        </Space>
      </Modal>

      <Modal title="备注" open={!!remarkRow} footer={null} onCancel={() => setRemarkRow(undefined)}>
        <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }}>{remarkRow?.remark}</Typography.Paragraph>
      </Modal>

      <Modal title="附加费明细" open={!!feeRow} footer={null} width={640} onCancel={() => setFeeRow(undefined)}>
        <AdaptiveTable
          size="small"
          rowKey={(_, i) => String(i)}
          pagination={false}
          dataSource={feeRow?.otherFees ?? []}
          columns={[
            { title: '费用', dataIndex: 'name', render: (v, r) => v || r.code || '-' },
            { title: '金额', dataIndex: 'amount', align: 'right', render: money },
            { title: '币种', dataIndex: 'currency' },
            { title: '金额(CNY)', dataIndex: 'amountCny', align: 'right', render: money },
            { title: '公式', dataIndex: 'formula' },
          ]}
        />
      </Modal>

      <Modal
        title={createRows.length > 1 ? `批量创建 KFC 渠道（${createRows.length} 个）` : '创建 KFC 渠道'}
        open={createRows.length > 0}
        confirmLoading={creating}
        onOk={submitCreate}
        onCancel={() => setCreateRows([])}
        okText="创建"
        width={640}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" preserve={false} initialValues={createInitial}>
          {createRows.length === 1 ? (
            <>
              <Form.Item name="agentId" label="代理">
                <Input disabled />
              </Form.Item>
              <Form.Item name="channelCode" label="渠道编号" rules={[{ required: true }]} extra="默认为代理渠道 serviceId，与渠道列表规则一致">
                <Input />
              </Form.Item>
              <Form.Item name="channelName" label="渠道名称">
                <Input />
              </Form.Item>
              <Form.Item name="carrierId" label="承运商">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={carriers.map((c) => ({ value: c.id, label: c.carrierName || c.carrierCode }))}
                />
              </Form.Item>
              <Form.Item name="volumeCoefficient" label="体积系数">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </>
          ) : (
            <AdaptiveTable
              size="small"
              rowKey={(r) => `${r.agentId}::${r.suggestedChannelCode}`}
              pagination={false}
              scroll={{ y: 300 }}
              dataSource={createRows}
              style={{ marginBottom: 12 }}
              columns={[
                { title: '代理', dataIndex: 'agentId', width: 80 },
                { title: '渠道编号', dataIndex: 'suggestedChannelCode', width: 90 },
                { title: '渠道名称', dataIndex: 'channelName' },
                { title: '承运商', dataIndex: 'carrierName', width: 90, render: (v) => v || '-' },
                { title: '体积系数', dataIndex: 'volumeDivisor', width: 80 },
              ]}
            />
          )}
          <Form.Item name="billingRuleId" label="计费重计算规则">
            <Select
              allowClear
              placeholder="可选"
              options={billingRules.map((r) => ({ value: r.id, label: r.name }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AgentPriceQuery;
